import React, { useState, useEffect } from 'react';
import { Text, View, TouchableOpacity, FlatList } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteDevice } from '@/data/settings';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import Toast from 'react-native-toast-message';
import { showAlert } from '@/utils/showAlert';
import Unicon from '@/components/General/Unicon';

const DevicesScreen = () => {
    /*** State ***/
    const [ currentDeviceID, setCurrentDeviceID ] = useState({})

    /*** Queries ***/
    const queryClient = useQueryClient()
    const { data: devices, isFetching } = Query.devices.all()
    const deleteDeviceMutation = useMutation({
        mutationFn: deleteDevice,
        onSuccess: () => {
            Toast.show({
                type: 'success',
                text2: `The device was successfully removed from your account`
            });
            
            queryClient.invalidateQueries({queryKey: QueryKeys.devices.all()})
        }
    });

    /*** Effects ***/
    useEffect(() => { 
        if(!isFetching){
            getDeviceID();
        }
    }, [isFetching])


    const getDeviceID = async () => {
        const currentDeviceID = await AsyncStorage.getItem("DEVICE_ID");
        setCurrentDeviceID(currentDeviceID)
    }

    const renderItem = ({item, index}) => {
        const containerStyles = [{...styles.settingContainer}];

        if(devices.length > 1){
            if(index < devices.length - 1){
                containerStyles.push({...styles.moreBelow})
            }

            if(index > 0){
                containerStyles.push({...styles.moreAbove})
            }
        }

        const deviceIcon = {
            0: "help",
            1: "phone-portrait",
            2: "tablet-portrait",
            3: "desktop"
        }

        return <View style={containerStyles}>
            <View style={styles.innerContainer}>
                <View style={styles.iconContainer}>
                    <Unicon name={deviceIcon[item.deviceType]} size={24} colorVariant="secondary" style={{width:35}} />
                    <View>
                        <Text style={styles.field_label}>{item.friendlyName}</Text>
                        <Text style={styles.field_value}>{item.modelName}</Text>
                        {item.id == currentDeviceID && <Text style={styles.thisDeviceText}>This device</Text> }
                    </View>
                </View>
            
                <TouchableOpacity style={{opacity:item.id == currentDeviceID ? 0.5 : 1}} onPress={() => {
                    if(item.id == currentDeviceID){
                        return false;
                    }

                    showAlert({
                        title: "Remove Device",
                        subtitle: `Are you sure you want to remove this device? This will force the device to sign out of your account`,
                        isDestructive: true,
                        onConfirm: () => {
                            deleteDeviceMutation.mutate(item.id)
                        },
                        confirmText: "Remove Device"
                    })
                }}>
                    <Text style={styles.removeDeviceText}>Remove Device</Text>
                </TouchableOpacity>
            </View>
        </View>
    }


    return (
        <View style={styles.container}>

            <View style={styles.listContainer}>
                <FlatList 
                    data={isFetching ? [] : devices}
                    renderItem={renderItem} 
                    style={{marginBottom: 50}}
                    progressViewOffset={1000}
                    refreshing={false}
                    onRefresh={() => {
                        queryClient.invalidateQueries({queryKey: QueryKeys.devices.all()})
                    }}
                />
            </View>

        </View> 
    );
}


const styles = StyleSheet.create(({colors, fonts}) => ({
    container:{
        padding:10, 
        flex: 1, 
        alignItems:"center" 
    },
    listContainer:{
        flex:1, 
        width:"100%", 
        maxWidth:700
    },
    settingContainer:{
        borderRadius:8, 
        backgroundColor:colors.surface,
        height:80,
        justifyContent:"center"
    },
    innerContainer: {
        padding:15, 
        flexDirection:"row", 
        justifyContent:"space-between", 
        alignItems:"center"
    },
    iconContainer:{
        flexDirection:"row", 
        alignItems:"center"
    },
    moreBelow:{
        borderBottomLeftRadius:0, 
        borderBottomRightRadius:0,
        borderBottomColor:colors.border, 
        borderBottomWidth:1
    },
    moreAbove:{
        borderTopLeftRadius:0, 
        borderTopRightRadius:0, 
    },
    field_label:{
        fontFamily:fonts.bold, 
        fontSize:15, 
        color:colors.text
    },
    field_value:{
        color:colors.textSecondary,
        fontSize:12,
    },
    thisDeviceText:{
        fontStyle:"italic", 
        fontSize:12, 
        color:colors.warning
    },
    removeDeviceText:{
        fontFamily:fonts.bold, 
        fontSize:12, 
        color:colors.error
    }
}))

export default DevicesScreen